<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useProductEditorContext } from '@/composition/catalog/use-product-editor';
  import { useReportingOptions } from '@/composition/catalog/use-reporting-options';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2CatalogReportingField from '../shared/V2CatalogReportingField.vue';

  /**
   * «Facturación» of the product (plan §5.12): «IVA (%)» («IGV (%)» in
   * Peru) and the reporting category.
   */
  const editor = useProductEditorContext();
  const { data } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string) => String(proxy.$t(key));
  const vatId = nextDomId('product-vat');
  const { leaves } = useReportingOptions(data.restaurant.value?.id ?? null);

  const form = computed(() => editor.form.value);
  const readOnly = computed(() => editor.readOnly.value);
  const vatLabel = computed(() =>
    t(
      editor.country.value === 'PE'
        ? 'menuPage.invoicing.igv'
        : 'menuPage.invoicing.vat'
    )
  );
  const vatError = computed(() =>
    editor.visibleErrors.value.vat ? t('menuPage.editor.errors.vat') : undefined
  );
</script>

<template>
  <div v-if="form" class="v2-catalog-invoicing-settings">
    <PosUiField
      class="invoicing-vat"
      :label="vatLabel"
      :input-id="vatId"
      :error="vatError"
      :hint="t('menuPage.invoicing.vatHint')"
    >
      <PosUiInput
        :id="vatId"
        class="vat-input"
        :value="form.vatText"
        inputmode="decimal"
        suffix="%"
        :maxlength="6"
        :disabled="readOnly"
        @input="editor.update('vatText', $event)"
      />
    </PosUiField>
    <V2CatalogReportingField
      class="invoicing-reporting"
      :value="form.reportingCategoryId"
      :leaves="leaves"
      :disabled="readOnly"
      @input="editor.update('reportingCategoryId', $event)"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-invoicing-settings {
    .vat-input {
      max-width: 160px;
    }

    .invoicing-reporting {
      @apply tw-mt-4;
    }
  }
</style>
